import React from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from 'react-router-dom'
import { router } from './app/router'
import { useThemeStore } from './stores/themeStore'
import { ErrorBoundary } from './app/ErrorBoundary'
// TODO: Replace with @bheem/design-client once npm auth is fixed
import { DesignProvider } from '@/lib/design-stub'
import './styles/index.css'

// Per-tenant design tokens flow from the orchestrator Design Service (brand-safe
// fallback if unreachable). Config is RUNTIME (window.__BHEEM_ENV__ injected by
// server.mjs) so one build serves every tenant; build-time env is the dev fallback.
const RT = (window as unknown as { __BHEEM_ENV__?: Record<string, string> }).__BHEEM_ENV__ || {}
const WORKSPACE_ID = RT.WORKSPACE_ID || import.meta.env.VITE_DESIGN_WORKSPACE_ID || undefined
const ORCHESTRATOR_URL = RT.ORCHESTRATOR_URL || import.meta.env.VITE_ORCHESTRATOR_URL || '/orchestrator-api'

// Initialize theme on app load
const initTheme = () => {
  const { mode } = useThemeStore.getState()
  const resolvedTheme = mode === 'system'
    ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
    : mode
  document.documentElement.classList.add(resolvedTheme)
}
initTheme()

// Single server-state cache for the whole ERP (replaces scattered fetch-on-mount).
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors (client errors)
        const status = (error as { response?: { status?: number } })?.response?.status
        if (status && status >= 400 && status < 500) return false
        // Retry up to 2 times for 5xx and network errors
        return failureCount < 2
      },
    },
  },
})

// Global API error handlers - show user-friendly notifications
if (typeof window !== 'undefined') {
  // Server error handler (502, 503, 504, 500)
  window.addEventListener('api:serverError', ((event: CustomEvent) => {
    const { status, message } = event.detail
    console.warn(`[App] Server error ${status}:`, message)
    // Show a toast notification if available, otherwise the error boundary will catch it
  }) as EventListener)

  // Network error handler
  window.addEventListener('api:networkError', ((event: CustomEvent) => {
    console.warn('[App] Network error:', event.detail.message)
  }) as EventListener)

  // Timeout handler
  window.addEventListener('api:timeout', ((event: CustomEvent) => {
    console.warn('[App] Timeout:', event.detail.message)
  }) as EventListener)
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <DesignProvider workspaceId={WORKSPACE_ID} orchestratorUrl={ORCHESTRATOR_URL} surface="dashboard" industry="edtech">
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </DesignProvider>
    </ErrorBoundary>
  </React.StrictMode>,
)
